import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';

const PAGE_LIMIT = 20;

export interface ImportablePlan {
    plan_id: string;
    plan_name: string;
}

interface RelatedPlans {
    parent: ImportablePlan[];
    child: ImportablePlan[];
    self: ImportablePlan[];
}

interface ListImportablePlansResponse {
    related_plans: RelatedPlans;
}

const fetchImportablePlans = async (
    planId: string,
    searchString: string,
    offset: number,
    signal?: AbortSignal,
): Promise<ListImportablePlansResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.listImportablePlans, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
        data: {
            plan_id: planId,
            search_string: searchString || null,
            limit: PAGE_LIMIT,
            offset,
        },
    });
    return response.data;
};

function dedupeById(plans: ImportablePlan[]): ImportablePlan[] {
    const seen = new Set<string>();
    const result: ImportablePlan[] = [];
    for (const p of plans) {
        if (!seen.has(p.plan_id)) {
            seen.add(p.plan_id);
            result.push(p);
        }
    }
    return result;
}

const useImportablePlans = ({ planId, searchString }: { planId: string; searchString: string }) => {
    const { data, isPending, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: tpoGenKeys.importablePlans(planId, searchString),
        queryFn: ({ signal, pageParam }) => fetchImportablePlans(planId, searchString, pageParam, signal),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const rp = lastPage.related_plans;
            const maxLen = Math.max(rp.parent.length, rp.child.length, rp.self.length);
            if (maxLen >= PAGE_LIMIT) {
                return lastPageParam + PAGE_LIMIT;
            }
            return undefined;
        },
        enabled: !!planId,
    });

    const plansByCategory = useMemo<RelatedPlans>(() => {
        if (!data?.pages?.length) return { parent: [], child: [], self: [] };
        const merged: RelatedPlans = { parent: [], child: [], self: [] };
        for (const page of data.pages) {
            merged.parent.push(...page.related_plans.parent);
            merged.child.push(...page.related_plans.child);
            merged.self.push(...page.related_plans.self);
        }
        return {
            parent: dedupeById(merged.parent),
            child: dedupeById(merged.child),
            self: dedupeById(merged.self),
        };
    }, [data]);

    return {
        plansByCategory,
        isPending,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage,
    };
};

export default useImportablePlans;
